// Copied from the VueUse lib
import type { EffectScope } from '@vue/composition-api';
import { effectScope } from '@vue/composition-api';

import { tryOnScopeDispose } from './tryOnScopeDispose';

/**
 * Make a composable function usable with multiple Vue instances.
 *
 * Existing composable state will be shared between all instances.
 * No new event listeners will be created, etc.
 *
 * Example:
 *   import { createdSharedComposable } from '@utils/createSharedComposable'
 *
 *   export const useSharedEventListener = createSharedComposable(useEventListener)
 */
export function createSharedComposable<Fn extends(...args: any[]) => any>(composable: Fn): Fn {
    let subscribers = 0;
    let state: ReturnType<Fn> | undefined;
    let scope: EffectScope | undefined;

    const dispose = () => {
        subscribers -= 1;

        if (scope && subscribers <= 0) {
            scope.stop();

            state = undefined;

            scope = undefined;
        }
    };

    return ((...args) => {
        subscribers += 1;

        if (!state) {
            scope = effectScope(true);

            state = scope.run(() => composable(...args));
        }

        tryOnScopeDispose(dispose);

        return state;
    }) as Fn;
}
